import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import LazyLoad from 'react-lazyload'
import ContactRequestButton from '~/components/LandingPage/components/ContactRequestButton'
import FeatureBlock from '~/components/LandingPage/components/FeatureBlock/FeatureBlock'
import HeroBanner from '~/components/LandingPage/HeroBanner'
import JourneySection from '~/components/LandingPage/JourneySection'
import TeamSection from '~/components/LandingPage/TeamSection'
import LayoutLandingPage from '~/components/Layout/LayoutLandingPage'

const LazyLoadWrapper = LazyLoad as unknown as FC<any>

const TrackAndCollaborateContainer: FC = () => {
  const { t } = useTranslation()

  return (
    <LayoutLandingPage>
      <HeroBanner
        badgeLabel={t('track_n_collaborate:label')}
        badgeColor="bg-blue-100 text-blue-800"
        title={t('track_n_collaborate:title')}
        description={t('track_n_collaborate:description')}
        images={{
          desktop: '/img/feature/lp/d-hire-better.png',
          mobile: '/img/feature/lp/m-hire-better.png'
        }}
        imageClassName="tablet:w-8/12"
      />

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[64px] mb-0 desktop:mt-[140px] desktop:mb-0"
          title={t('track_n_collaborate:better_decision')}
          description={t('track_n_collaborate:better_decision_description')}
          openLink={<ContactRequestButton />}
          image={
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/d-track-collaborate.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/m-track-collaborate.png"
              />
              <img
                alt="Collaborative hiring for better decision"
                loading="lazy"
              />
            </picture>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[64px] mb-0 desktop:mt-[140px] desktop:mb-0"
          title={t('track_n_collaborate:consistent_interview')}
          description={t(
            'track_n_collaborate:consistent_interview_description'
          )}
          textPosition="right"
          openLink={<ContactRequestButton />}
          image={
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/consistent-interview.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/consistent-interview-mweb.png"
              />
              <img alt="Consistent interview and evaluation" loading="lazy" />
            </picture>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[100px] tablet:mt-[75px]"
          title={t('track_n_collaborate:easy_build_engage')}
          description={t('track_n_collaborate:easy_build_engage_description')}
          textPosition="left"
          openLink={<ContactRequestButton />}
          image={
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/easy-build.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/easy-build-mweb.png"
              />
              <img
                alt="Easy build and engage with talent pool"
                loading="lazy"
              />
            </picture>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[64px] mb-0 desktop:mt-[140px] desktop:mb-0"
          title={t('track_n_collaborate:move_candidates')}
          description={t('track_n_collaborate:move_candidates_description')}
          textPosition="right"
          openLink={<ContactRequestButton />}
          image={
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/move-candidates.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/move-candidates-mweb.png"
              />
              <img
                alt="Move candidates through the process quickly"
                loading="lazy"
              />
            </picture>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[64px] mb-0 desktop:mt-[140px] desktop:mb-0"
          title={t('track_n_collaborate:everything_about_candidate')}
          description={t(
            'track_n_collaborate:everything_about_candidate_description'
          )}
          textPosition="top"
          openLink={<ContactRequestButton />}
          image={
            <div className="flex flex-col tablet:flex-row gap-4 tablet:gap-5">
              <img
                src="/img/feature/lp/candidate-in-1-place-1.png"
                alt="Everything about your candidate in one place"
                className="w-full tablet:w-[calc(60%_-_10px)]"
                loading="lazy"
              />
              <img
                src="/img/feature/lp/candidate-in-1-place-2.png"
                alt="Everything about your candidate in one place"
                className="w-full tablet:w-[calc(40%_-_10px)]"
                loading="lazy"
              />
            </div>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <FeatureBlock
          className="mt-[64px] mb-0 desktop:mt-[140px] desktop:mb-0"
          title={t('track_n_collaborate:effortlessly_schedule_interview')}
          description={t(
            'track_n_collaborate:effortlessly_schedule_interview_description'
          )}
          textPosition="right"
          openLink={<ContactRequestButton />}
          image={
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/effortlessly-interview.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/effortlessly-interview-mweb.png"
              />
              <img
                alt="Effortlessly schedule interview with candidate"
                loading="lazy"
              />
            </picture>
          }
        />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <TeamSection />
      </LazyLoadWrapper>

      <LazyLoadWrapper height={500} once>
        <JourneySection className="desktop:mt-[140px] desktop:mb-[0px] bg-gray-50 mt-[64px] my-[0px] py-[64px]" />
      </LazyLoadWrapper>
    </LayoutLandingPage>
  )
}

export default TrackAndCollaborateContainer
